import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Shield, 
  Droplets, 
  Car, 
  CheckCircle,
  Sparkles,
  Award,
  Zap
} from "lucide-react";
import xpelLogo from "@/assets/xpel-logo.png";

const TeslaProtection = () => {

  const teslaFeatures = [
    {
      icon: Droplets,
      title: "Soft Paint Composition",
      description: "Tesla's water-based paint is thinner and softer than traditional automotive paint, requiring specialized protection."
    },
    {
      icon: Sparkles,
      title: "Prone to Swirl Marks",
      description: "Even light washing can cause micro-scratches visible in sunlight—PPF prevents contact damage."
    },
    {
      icon: Zap,
      title: "Rock Chip Vulnerability",
      description: "Front-end damage occurs faster due to paint softness. Self-healing PPF absorbs impacts."
    },
    {
      icon: Award,
      title: "Resale Value Protection",
      description: "Tesla owners expect pristine paint. Protection maintains trade-in value and buyer confidence."
    }
  ];

  const packages = [
    {
      name: "Essential",
      subtitle: "Full Front",
      coverage: ["Hood", "Front bumper", "Fenders", "Mirror caps", "Headlights"],
      ideal: "Budget-conscious protection",
      badge: null
    },
    {
      name: "Daily Driver",
      subtitle: "Full Front + Rockers",
      coverage: ["Everything in Essential", "Rocker panels", "A-pillars", "Door edges"],
      ideal: "Daily commuters on I-15/215",
      badge: "Most Popular"
    },
    {
      name: "Stealth",
      subtitle: "Full Matte Conversion",
      coverage: ["Full body coverage", "XPEL Stealth film", "Satin finish transformation", "Complete protection"],
      ideal: "Transform to a matte finish",
      badge: "Premium"
    }
  ];

  const teslaModels = [
    "Model 3 (All Years)",
    "Model Y (All Years)",
    "Model S (All Years)",
    "Model X (All Years)",
    "Cybertruck (Custom Templates)"
  ];

  return (
    <>
      <Helmet>
        <title>Tesla PPF Las Vegas | Protection for Model 3, Y, S, X | Battle Born</title>
        <meta 
          name="description" 
          content="Specialized paint protection film and ceramic coating packages for Tesla vehicles in Las Vegas. Expert installation for soft Tesla paint. Free quote." 
        />
        <meta property="og:title" content="Tesla PPF Las Vegas | Battle Born Auto Protect" />
        <meta property="og:description" content="The ultimate protection for your Tesla. Specialized PPF packages for soft Tesla paint." />
        <link rel="canonical" href="https://battleborn-autoprotect.lovable.app/tesla-protection" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Tesla Paint Protection Film",
            "provider": {
              "@type": "LocalBusiness",
              "name": "Battle Born Auto Protect",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Las Vegas",
                "addressRegion": "NV"
              }
            },
            "areaServed": "Las Vegas, NV",
            "description": "Specialized paint protection film packages for Tesla Model 3, Y, S, X, and Cybertruck."
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center bg-gradient-to-b from-black via-zinc-900 to-background overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-accent/10 via-transparent to-transparent" />
        <div className="container mx-auto px-4 py-20 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/30 rounded-full px-4 py-2 mb-6">
            <Zap className="w-4 h-4 text-accent" />
            <span className="text-sm text-accent font-medium">Tesla Specialists</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground mb-6 leading-tight">
            The Ultimate Protection for Your{" "}
            <span className="text-accent">Tesla</span> in Las Vegas
          </h1>
          
          <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Specialized PPF & Ceramic Packages for Soft Tesla Paint
          </p>
          
          <Button 
            asChild 
            variant="hero" 
            size="lg" 
            className="min-h-[48px] text-base"
          >
            <Link to="/contact">Get Your Tesla Quote</Link>
          </Button>
        </div>
      </section>

      {/* Why Teslas Need Special Care */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Teslas Need <span className="text-accent">Special Care</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Tesla's innovative paint technology requires specialized protection to maintain its showroom finish.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {teslaFeatures.map((feature, index) => (
              <div 
                key={index}
                className="flex gap-4 p-6 bg-card rounded-lg border border-white/10"
              >
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <feature.icon className="w-6 h-6 text-accent" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PPF Packages */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Tesla Protection <span className="text-accent">Packages</span>
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Choose the level of protection that fits your driving lifestyle.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {packages.map((pkg, index) => (
              <Card 
                key={index}
                className="relative bg-[#2A2A2A] border-white/10 overflow-hidden group hover:border-accent/50 transition-all duration-300"
              >
                {pkg.badge && (
                  <div className="absolute top-4 right-4 bg-accent text-accent-foreground text-xs font-bold px-3 py-1 rounded-full">
                    {pkg.badge}
                  </div>
                )}
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl font-heading text-foreground">
                    {pkg.name}
                  </CardTitle>
                  <p className="text-accent font-medium">{pkg.subtitle}</p>
                </CardHeader>
                <CardContent className="space-y-4">
                  <ul className="space-y-2">
                    {pkg.coverage.map((item, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="pt-4 border-t border-white/10">
                    <p className="text-xs text-muted-foreground">
                      <span className="text-accent">Ideal for:</span> {pkg.ideal}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>


      {/* Trust Signals */}
      <section className="py-16 bg-[#1A1A1A] border-y border-white/10">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-heading font-bold text-accent mb-1">
                1,000+
              </div>
              <p className="text-sm text-muted-foreground">Teslas Protected</p>
            </div>
            
            <div className="h-12 w-px bg-white/10 hidden md:block" />
            
            <div className="flex items-center gap-3">
              <img 
                src={xpelLogo} 
                alt="XPEL Certified Installer" 
                className="h-10 w-auto"
                loading="lazy"
              />
              <div>
                <p className="text-sm font-medium text-foreground">XPEL Certified</p>
                <p className="text-xs text-muted-foreground">Installer</p>
              </div>
            </div>
            
            <div className="h-12 w-px bg-white/10 hidden md:block" />
            
            <div className="flex items-center gap-3">
              <Car className="w-8 h-8 text-accent" />
              <div>
                <p className="text-sm font-medium text-foreground">Soft Paint</p>
                <p className="text-xs text-muted-foreground">Specialists</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tesla Model Compatibility */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-8">
              All Tesla Models <span className="text-accent">Supported</span>
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {teslaModels.map((model, index) => (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-4 bg-card rounded-lg border border-white/10"
                >
                  <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                  <span className="text-foreground">{model}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-b from-[#1A1A1A] to-background">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
            Ready to Protect Your <span className="text-accent">Tesla</span>?
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8">
            Get a custom quote for your specific model and coverage needs. Our Tesla specialists are ready to help.
          </p>
          <Button 
            asChild 
            variant="premium" 
            size="lg"
            className="min-h-[48px]"
          >
            <Link to="/contact">Request Free Tesla Quote</Link>
          </Button>
        </div>
      </section>

      {/* Sticky Mobile CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur-sm border-t border-white/10 lg:hidden z-50">
        <Button 
          asChild 
          variant="hero" 
          className="w-full min-h-[56px] text-base"
        >
          <Link to="/contact">Get Your Tesla Quote</Link>
        </Button>
      </div>

      {/* Spacer for sticky CTA on mobile */}
      <div className="h-24 lg:hidden" />
    </>
  );
};

export default TeslaProtection;
